import React, { useState } from 'react';
import Select, { type SingleValue } from 'react-select';

// Utils
import { formatTime, getTime } from 'utils/string';
import { generateReactSelectOptions } from 'utils/reactSelect';

const TIME_OPTIONS = ['AM', 'PM'] as const;

type TimeType = (typeof TIME_OPTIONS)[number];

type TimeOption = {
  value: TimeType;
  label: TimeType;
};

const isTimeType = (value: string): value is TimeType => value === 'AM' || value === 'PM';

interface TimeValue {
  type: TimeType;
  value: string;
}

type TimeInputChangeEvent = {
  target: {
    name?: string;
    value: {
      value: string;
      type: TimeType;
    };
  };
};

type TimeInputBlurEvent = {
  target: {
    name?: string;
  };
};

interface Props {
  label?: string;
  error?: React.ReactNode;
  isMandatory?: boolean;
  name?: string;
  placeholder?: string;
  type?: string;
  time: TimeValue;
  disabled?: boolean;
  onChange: (e: TimeInputChangeEvent) => void;
  onBlur: (e: TimeInputBlurEvent) => void;
}

const TimeInput: React.FC<Props> = ({
  label: _label,
  isMandatory: _isMandatory,
  name,
  placeholder,
  type,
  time,
  disabled,
  error,
  onChange,
  onBlur,
  ...rest
}) => {
  const normalizedType = time.type.toUpperCase();
  const propTimeType = isTimeType(normalizedType) ? normalizedType : 'AM';
  const propInputValue = formatTime(time.value, propTimeType);
  const sourceKey = `${time.value}-${propTimeType}`;
  const [draft, setDraft] = useState(() => ({
    sourceKey,
    timeType: propTimeType,
    inputValue: propInputValue,
  }));
  const isDraftCurrent = draft.sourceKey === sourceKey;
  const timeType = isDraftCurrent ? draft.timeType : propTimeType;
  const inputValue = isDraftCurrent ? draft.inputValue : propInputValue;

  const handleTimeChange = (nextInputValue = inputValue, nextTimeType = timeType) => {
    const formattedTime = formatTime(nextInputValue, nextTimeType);
    const parsedTime = getTime(formattedTime);
    const normalizedType = parsedTime.type.toUpperCase();
    const nextType = isTimeType(normalizedType) ? normalizedType : 'AM';

    setDraft({
      sourceKey,
      timeType: nextType,
      inputValue: formattedTime,
    });

    onChange({
      target: {
        name,
        value: {
          value: parsedTime.value,
          type: nextType,
        },
      },
    });
  };

  const handleBlur = () => {
    onBlur({
      target: {
        name,
      },
    });
    handleTimeChange();
  };

  return (
    <div className="time-input">
      <input
        className={`time-input__text ${error ? 'input-wrapper__input--error' : ''}`}
        placeholder={placeholder}
        type={type}
        value={inputValue}
        disabled={disabled}
        onChange={(e) =>
          setDraft({
            sourceKey,
            timeType,
            inputValue: e.target.value,
          })
        }
        onBlur={handleBlur}
        {...rest}
      />
      <Select<TimeOption, false>
        isDisabled={disabled}
        classNamePrefix="time-select"
        value={{ value: timeType, label: timeType }}
        options={generateReactSelectOptions([...TIME_OPTIONS]) as TimeOption[]}
        onChange={(option: SingleValue<TimeOption>) => {
          if (!option) {
            return;
          }

          handleTimeChange(inputValue, option.value);
        }}
        onBlur={handleBlur}
      />
    </div>
  );
};

export default TimeInput;
